<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { pipelineAPI } from 'dashboard/api/pipeline';
import { usePainelDaConversa } from 'dashboard/composables/usePainelDaConversa';
import {
  serveNoKanban,
  etiquetasParaEscolher,
} from 'dashboard/helper/escopoDaEtiqueta';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const store = useStore();
const router = useRouter();
const { oculto } = usePainelDaConversa();
const { t } = useI18n();
const tCard = (chave, params) => t(`KANBAN.CARD_DA_CONVERSA.${chave}`, params);
const card = ref(null);
const loading = ref(false);
const saving = ref(false);

const accountId = store.getters.getCurrentAccountId;
const allAgents = computed(() => store.getters['agents/getAgents'] || []);
const accountLabels = computed(() => store.getters['labels/getLabels'] || []);

// ── Produtos do card (declarados antes: fetchCard os carrega) ────────────
const products = ref([]);
const catalogProducts = ref([]);
const loadingProducts = ref(false);
const showAddProduct = ref(false);
const pickProductId = ref(null);
const pickQty = ref(1);
const pickDiscount = ref(0);

const fetchProducts = async () => {
  if (!card.value?.cardId) return;
  loadingProducts.value = true;
  try {
    const { data } = await pipelineAPI.getCardProducts(
      card.value.cardId,
      accountId
    );
    products.value = data || [];
  } catch {
    products.value = [];
  } finally {
    loadingProducts.value = false;
  }
};

// ── Fetch ──────────────────────────────────────────────────────────────────
const fetchCard = async id => {
  if (!id) return;
  loading.value = true;
  card.value = null;
  try {
    const r = await window.axios.get(
      `/api/v1/accounts/${accountId}/conversations/${id}/pipeline_card`
    );
    card.value = r.data;
    // Busca o card completo para obter contacts (detail DTO só tem contactIds)
    if (card.value?.cardId) {
      try {
        const { data: fullCard } = await pipelineAPI.getCard(
          card.value.cardId,
          accountId
        );
        card.value = { ...card.value, contacts: fullCard.contacts || [] };
      } catch {
        /* contacts are optional */
      }
    }
    await fetchProducts();
  } catch {
    // 404 = sem card vinculado
  } finally {
    loading.value = false;
  }
};

// ── Criar card manualmente ─────────────────────────────────────────────────
const showCreateForm = ref(false);
const createPipelines = ref([]);
const createStages = ref([]);
const createPipelineId = ref(null);
const createStageId = ref(null);
const createTitle = ref('');
const creating = ref(false);
const createError = ref('');

const loadPipelines = async () => {
  if (createPipelines.value.length) return;
  try {
    const { data } = await pipelineAPI.getOverview(
      accountId,
      store.getters.getCurrentUser?.id
    );
    createPipelines.value = data || [];
  } catch {
    createPipelines.value = [];
  }
};

const onCreatePipelineChange = async () => {
  createStageId.value = null;
  createStages.value = [];
  if (!createPipelineId.value) return;
  try {
    const { data } = await pipelineAPI.getStages(
      createPipelineId.value,
      accountId
    );
    createStages.value = (data || []).filter(s => !s.isWon && !s.isLost);
    if (createStages.value.length)
      createStageId.value = createStages.value[0].id;
  } catch {
    createStages.value = [];
  }
};

const openCreateForm = async () => {
  await loadPipelines();
  createPipelineId.value = null;
  createStageId.value = null;
  createStages.value = [];
  createError.value = '';
  showCreateForm.value = true;
};

const cancelCreate = () => {
  showCreateForm.value = false;
};

const submitCreate = async () => {
  if (!createPipelineId.value) return;
  creating.value = true;
  createError.value = '';
  try {
    // Roteia pelo proxy Rails (mesma origem). O Rails resolve a conversa e o
    // contato de forma autoritativa (conversation.id real + dados do contato),
    // então o frontend só envia título e pipeline/etapa escolhidos.
    await window.axios.post(
      `/api/v1/accounts/${accountId}/conversations/${props.conversationId}/pipeline_card`,
      {
        title: createTitle.value.trim() || null,
        targetPipelineId: createPipelineId.value,
        targetStageId: createStageId.value || null,
      }
    );
    showCreateForm.value = false;
    createTitle.value = '';
    await fetchCard(props.conversationId);
  } catch (e) {
    createError.value = e?.response?.data?.error || tCard('CREATE_ERROR');
    // eslint-disable-next-line no-console
    console.error('[PipelineKanbanCard] erro ao criar card', e);
  } finally {
    creating.value = false;
  }
};

onMounted(() => {
  if (!accountLabels.value.length) store.dispatch('labels/get');
});

// ── Overdue ────────────────────────────────────────────────────────────────
const isOverdue = computed(() => {
  const d = card.value?.dueDate;
  const s = card.value?.status;
  return d && new Date(d) < new Date() && (!s || s === 'OPEN');
});

watch(() => props.conversationId, fetchCard, { immediate: true });

// ── Edição inline ──────────────────────────────────────────────────────────
const editField = async (field, value) => {
  if (!card.value?.cardId) return;
  saving.value = true;
  try {
    await window.axios.patch(
      `/api/v1/accounts/${accountId}/conversations/${props.conversationId}/pipeline_card`,
      { card_id: card.value.cardId, [field]: value }
    );
    card.value[field] = value;
    if (field === 'stageId') {
      const stage = card.value.stages?.find(s => s.id === value);
      if (stage) card.value.stageName = stage.name;
    }
  } finally {
    saving.value = false;
  }
};

// ── Link para o Kanban ─────────────────────────────────────────────────────
const goToKanban = () => {
  router.push(`/app/accounts/${accountId}/kanban?cardId=${card.value.cardId}`);
};

// ── Stage ──────────────────────────────────────────────────────────────────
const onStageChange = e => editField('stageId', Number(e.target.value));

// ── Cor do stage ──────────────────────────────────────────────────────────
const stageColor = computed(() => {
  const s = card.value?.stages?.find(x => x.id === card.value.stageId);
  return s?.color || '#475569';
});

// ── Prioridade ─────────────────────────────────────────────────────────────
const PRIORITIES = [
  { value: 'none', labelKey: 'NONE', color: 'text-n-slate-9' },
  { value: 'low', labelKey: 'LOW', color: 'text-green-500' },
  { value: 'medium', labelKey: 'MEDIUM', color: 'text-yellow-500' },
  { value: 'high', labelKey: 'HIGH', color: 'text-orange-500' },
  { value: 'urgent', labelKey: 'URGENT', color: 'text-red-500' },
];
const priorityColor = computed(() => {
  const p = PRIORITIES.find(x => x.value === card.value?.priority);
  return p?.color || 'text-n-slate-9';
});
const onPriorityChange = e =>
  editField('priority', e.target.value === 'none' ? null : e.target.value);

// ── Agentes (somente leitura no sidebar) ───────────────────────────────────
const assignedAgents = computed(() =>
  (card.value?.assigneeIds || [])
    .map(id => allAgents.value.find(a => a.id === id))
    .filter(Boolean)
);

// ── Datas ──────────────────────────────────────────────────────────────────
const fmtDatePart = iso => (iso ? iso.slice(0, 10) : '');
const fmtTimePart = iso => (iso ? iso.slice(11, 16) || '00:00' : '00:00');

const onDatePartChange = (field, datePart, e) => {
  const d = e.target.value;
  const existing = card.value?.[field];
  const hora = existing ? existing.slice(11, 16) : '00:00';
  editField(field, d ? `${d}T${hora}:00` : null);
};
const onTimePartChange = (field, timePart, e) => {
  const hora = e.target.value;
  const existing = card.value?.[field];
  const d = existing ? existing.slice(0, 10) : '';
  if (d) editField(field, `${d}T${hora || '00:00'}:00`);
};

// ── Labels (sistema de etiquetas do Chatwoot) ─────────────────────────────
const showLabelDropdown = ref(false);
const labelSearch = ref('');

const activeCardLabels = computed(() =>
  accountLabels.value.filter(al =>
    (card.value?.labels || []).includes(al.title)
  )
);
// Só as etiquetas do Kanban (+ as que o card já tem, para poder retirar).
const kanbanLabels = computed(() =>
  etiquetasParaEscolher(
    accountLabels.value,
    serveNoKanban,
    card.value?.labels || []
  )
);
const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return q
    ? kanbanLabels.value.filter(l => l.title.toLowerCase().includes(q))
    : kanbanLabels.value;
});
const isLabelActive = title => (card.value?.labels || []).includes(title);

const toggleLabel = title => {
  const current = card.value?.labels || [];
  const next = current.includes(title)
    ? current.filter(l => l !== title)
    : [...current, title];
  editField('labels', next);
};

// ── Oportunidade (Valor + Produtos) — accordion ───────────────────────────
const oportunidadeOpen = ref(true);

// Valor
const editingValue = ref(false);
const valueInput = ref('');
const startEditValue = () => {
  valueInput.value = card.value?.value ?? '';
  editingValue.value = true;
};
const saveValue = () => {
  editField('value', valueInput.value || null);
  editingValue.value = false;
};

// Produtos
const loadCatalog = async () => {
  if (catalogProducts.value.length || !card.value?.pipelineId) return;
  try {
    const { data } = await pipelineAPI.getProducts(
      card.value.pipelineId,
      accountId,
      false
    );
    catalogProducts.value = data || [];
  } catch {
    catalogProducts.value = [];
  }
};

const openAddProduct = async () => {
  await loadCatalog();
  pickProductId.value = null;
  pickQty.value = 1;
  pickDiscount.value = 0;
  showAddProduct.value = true;
};

const confirmAddProduct = async () => {
  if (!pickProductId.value) return;
  const prod = catalogProducts.value.find(p => p.id === pickProductId.value);
  if (!prod) return;
  saving.value = true;
  try {
    await pipelineAPI.addCardProduct(
      card.value.cardId,
      {
        productId: prod.id,
        quantity: pickQty.value,
        unitPrice: prod.unitPrice,
        discountPercentage: pickDiscount.value,
      },
      accountId
    );
    await fetchProducts();
    showAddProduct.value = false;
  } finally {
    saving.value = false;
  }
};

const removeProduct = async itemId => {
  saving.value = true;
  try {
    await pipelineAPI.deleteCardProduct(card.value.cardId, itemId, accountId);
    await fetchProducts();
  } finally {
    saving.value = false;
  }
};

const productSubtotal = item => {
  const qty = parseFloat(item.quantity) || 0;
  const price = parseFloat(item.unitPrice) || 0;
  const disc = parseFloat(item.discountPercentage) || 0;
  return qty * price * (1 - disc / 100);
};

const productsTotal = computed(() =>
  products.value.reduce((s, i) => s + productSubtotal(i), 0)
);

const fmtMoney = val =>
  Number(val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
</script>

<template>
  <div class="px-4 py-3">
    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center gap-2 text-xs text-n-slate-9 py-2"
    >
      <div
        class="w-3 h-3 border border-n-blue-9 border-t-transparent rounded-full animate-spin"
      />
      {{ tCard('LOADING') }}
    </div>

    <!-- Sem card -->
    <div v-else-if="!card" class="flex flex-col gap-3">
      <p class="text-xs text-n-slate-9 italic">
        {{ tCard('NO_CARD') }}
      </p>

      <!-- Formulário de criação -->
      <div
        v-if="showCreateForm"
        class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg bg-n-surface-1"
      >
        <p
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('ADD_TO_KANBAN') }}
        </p>

        <!-- Título opcional -->
        <div class="flex flex-col gap-0.5">
          <label class="text-[10px] text-n-slate-9 uppercase">{{
            tCard('TITLE_OPTIONAL')
          }}</label>
          <input
            v-model="createTitle"
            type="text"
            class="!mb-0 text-xs py-1 w-full"
            :placeholder="tCard('TITLE_PLACEHOLDER')"
          />
        </div>

        <!-- Pipeline -->
        <div class="flex flex-col gap-0.5">
          <label class="text-[10px] text-n-slate-9 uppercase">{{
            tCard('PIPELINE_REQUIRED')
          }}</label>
          <select
            v-model="createPipelineId"
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-surface-1"
            @change="onCreatePipelineChange"
          >
            <option :value="null" disabled>
              {{ tCard('SELECT_PIPELINE') }}
            </option>
            <option v-for="p in createPipelines" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
          <p
            v-if="!createPipelines.length"
            class="text-[10px] text-n-slate-9 italic"
          >
            {{ tCard('NO_PIPELINES') }}
          </p>
        </div>

        <!-- Etapa -->
        <div v-if="createStages.length" class="flex flex-col gap-0.5">
          <label class="text-[10px] text-n-slate-9 uppercase">{{
            tCard('STAGE')
          }}</label>
          <select
            v-model="createStageId"
            class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-surface-1"
          >
            <option v-for="s in createStages" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>
        <p
          v-else-if="createPipelineId && !createStages.length"
          class="text-[10px] text-n-slate-9 italic"
        >
          {{ tCard('LOADING_STAGES') }}
        </p>

        <div class="flex gap-1 pt-1">
          <button
            class="flex-1 py-1.5 text-xs rounded bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50 font-medium"
            :disabled="!createPipelineId || creating"
            @click="submitCreate"
          >
            {{ creating ? tCard('CREATING') : tCard('CREATE_CARD') }}
          </button>
          <button
            class="flex-1 py-1.5 text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="cancelCreate"
          >
            {{ tCard('CANCEL') }}
          </button>
        </div>

        <p
          v-if="createError"
          class="text-[10px] text-red-600 font-medium pt-0.5"
        >
          {{ createError }}
        </p>
      </div>

      <button
        v-else
        class="w-full py-2 text-xs rounded-lg border border-dashed border-n-blue-9 text-n-blue-9 hover:bg-n-blue-9 hover:text-white transition-colors font-medium"
        @click="openCreateForm"
      >
        + {{ tCard('ADD_TO_KANBAN') }}
      </button>
    </div>

    <!-- Card completo -->
    <div v-else class="flex flex-col gap-3">
      <!-- Banner: card vencido -->
      <div
        v-if="isOverdue"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-300 text-red-700 text-xs font-medium"
      >
        <span class="i-lucide-triangle-alert size-4" />
        <span>{{
          tCard('OVERDUE', {
            date: new Date(card.dueDate).toLocaleDateString('pt-BR'),
          })
        }}</span>
      </div>

      <!-- Título + link para o Kanban -->
      <div>
        <p
          class="text-[10px] font-bold text-n-slate-9 uppercase tracking-wide mb-0.5"
        >
          {{ card.pipelineName }}
        </p>
        <button
          class="text-sm font-semibold text-n-blue-9 hover:underline text-left leading-tight cursor-pointer"
          :title="tCard('OPEN_IN_KANBAN')"
          @click="goToKanban"
        >
          {{ card.title }}
          <span
            class="i-lucide-external-link size-3 inline-block align-middle"
          />
        </button>
      </div>

      <!-- Stage -->
      <div class="flex flex-col gap-1">
        <label
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('STAGE') }}
        </label>
        <div class="relative">
          <span
            class="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
            :style="{ background: stageColor }"
          />
          <select
            :value="card.stageId"
            class="w-full pl-6 pr-2 py-1.5 text-xs rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 cursor-pointer"
            :disabled="saving"
            @change="onStageChange"
          >
            <option v-for="s in card.stages" :key="s.id" :value="s.id">
              {{ s.name }}{{ s.isWon ? ' ✓' : s.isLost ? ' ✗' : '' }}
            </option>
          </select>
        </div>
      </div>

      <!-- Prioridade -->
      <div class="flex flex-col gap-1">
        <label
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('PRIORITY') }}
        </label>
        <select
          :value="card.priority || 'none'"
          class="w-full px-2 py-1.5 text-xs rounded-lg border border-n-weak bg-n-surface-1 cursor-pointer"
          :class="priorityColor"
          :disabled="saving"
          @change="onPriorityChange"
        >
          <option
            v-for="p in PRIORITIES"
            :key="p.value"
            :value="p.value"
            :class="p.color"
          >
            {{ tCard(`PRIORITIES.${p.labelKey}`) }}
          </option>
        </select>
      </div>

      <!-- Etiquetas (labels do Chatwoot) -->
      <div class="flex flex-col gap-1">
        <label
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('LABELS') }}
        </label>
        <p class="mb-0 -mt-0.5 text-[10px] text-n-slate-9">
          {{ tCard('LABELS_HINT') }}
        </p>

        <!-- Chips das labels ativas -->
        <div class="flex flex-wrap gap-1 min-h-[1.25rem]">
          <span
            v-for="lbl in activeCardLabels"
            :key="lbl.id"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-white font-medium cursor-pointer"
            :style="{ background: lbl.color }"
            @click="toggleLabel(lbl.title)"
          >
            {{ lbl.title }}
            <span class="i-lucide-x size-3" :title="tCard('REMOVE_LABEL')" />
          </span>
          <button
            class="px-2 py-0.5 rounded-full text-[10px] border border-dashed border-n-weak text-n-slate-9 hover:border-n-blue-9 hover:text-n-blue-9"
            @click="showLabelDropdown = !showLabelDropdown"
          >
            {{
              showLabelDropdown
                ? tCard('CLOSE_LABELS')
                : `+ ${tCard('ADD_LABEL')}`
            }}
          </button>
        </div>

        <!-- Dropdown de seleção -->
        <div
          v-if="showLabelDropdown"
          class="border border-n-weak rounded-lg bg-n-surface-1 shadow-md p-2 flex flex-col gap-1.5"
        >
          <input
            v-model="labelSearch"
            type="text"
            class="!mb-0 text-xs py-1 w-full"
            :placeholder="tCard('SEARCH_LABEL')"
          />
          <div class="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
            <button
              v-for="lbl in filteredLabels"
              :key="lbl.id"
              class="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-colors cursor-pointer"
              :style="
                isLabelActive(lbl.title)
                  ? {
                      background: lbl.color,
                      borderColor: lbl.color,
                      color: '#fff',
                    }
                  : {
                      borderColor: lbl.color,
                      color: lbl.color,
                      background: 'transparent',
                    }
              "
              @click="toggleLabel(lbl.title)"
            >
              <span
                class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                :style="{
                  background: isLabelActive(lbl.title) ? '#fff' : lbl.color,
                }"
              />
              {{ lbl.title }}
            </button>
            <p
              v-if="!filteredLabels.length"
              class="text-xs text-n-slate-9 italic px-1"
            >
              {{ tCard('NO_LABELS_FOUND') }}
            </p>
          </div>
        </div>
        <p
          v-if="!kanbanLabels.length"
          class="text-[10px] text-n-slate-9 italic"
        >
          {{ tCard('CREATE_LABELS_HINT') }}
        </p>
      </div>

      <!-- Agentes atribuídos (somente leitura) -->
      <div class="flex flex-col gap-1">
        <label
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('ASSIGNED_AGENTS') }}
        </label>
        <div class="flex flex-wrap gap-1">
          <span
            v-for="agent in assignedAgents"
            :key="agent.id"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border bg-n-blue-9 border-n-blue-9 text-white"
          >
            {{ agent.name }}
          </span>
          <span
            v-if="!assignedAgents.length"
            class="text-[10px] text-n-slate-9 italic"
          >
            {{ tCard('NO_AGENTS') }}
          </span>
        </div>
      </div>

      <!-- Contatos vinculados (somente leitura) -->
      <div v-if="!oculto('kanban_contatos')" class="flex flex-col gap-1">
        <label
          class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ tCard('CONTACTS') }}
        </label>
        <div class="flex flex-col gap-1">
          <div
            v-for="contact in card.contacts || []"
            :key="contact.id"
            class="flex flex-col px-2 py-1.5 rounded-lg border border-n-weak bg-n-surface-1"
          >
            <span class="text-xs font-medium text-n-slate-12">{{
              contact.name || tCard('CONTACT_FALLBACK', { id: contact.id })
            }}</span>
            <span v-if="contact.email" class="text-[10px] text-n-slate-9">{{
              contact.email
            }}</span>
            <span v-if="contact.phone" class="text-[10px] text-n-slate-9">{{
              contact.phone
            }}</span>
          </div>
          <span
            v-if="!(card.contacts || []).length"
            class="text-[10px] text-n-slate-9 italic"
          >
            {{ tCard('NO_CONTACTS') }}
          </span>
        </div>
      </div>

      <!-- Datas -->
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-1">
          <label
            class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
          >
            {{ tCard('START') }}
          </label>
          <div class="flex gap-1">
            <input
              type="date"
              :value="fmtDatePart(card.startDate)"
              class="!mb-0 text-xs py-1 flex-1"
              :disabled="saving"
              @change="
                e =>
                  onDatePartChange('startDate', fmtTimePart(card.startDate), e)
              "
            />
            <input
              type="time"
              :value="fmtTimePart(card.startDate)"
              class="!mb-0 text-xs py-1 w-24"
              :disabled="saving"
              @change="
                e =>
                  onTimePartChange('startDate', fmtDatePart(card.startDate), e)
              "
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="text-[10px] font-bold uppercase tracking-wide"
            :class="isOverdue ? 'text-red-600' : 'text-n-slate-11'"
          >
            {{ tCard('DUE') }}
            <span
              v-if="isOverdue"
              class="i-lucide-triangle-alert size-3 inline-block align-middle"
            />
          </label>
          <div class="flex gap-1">
            <input
              type="date"
              :value="fmtDatePart(card.dueDate)"
              class="!mb-0 text-xs py-1 flex-1"
              :class="{ 'border-red-400': isOverdue }"
              :disabled="saving"
              @change="
                e => onDatePartChange('dueDate', fmtTimePart(card.dueDate), e)
              "
            />
            <input
              type="time"
              :value="fmtTimePart(card.dueDate)"
              class="!mb-0 text-xs py-1 w-24"
              :class="{ 'border-red-400 text-red-700': isOverdue }"
              :disabled="saving"
              @change="
                e => onTimePartChange('dueDate', fmtDatePart(card.dueDate), e)
              "
            />
          </div>
        </div>
      </div>

      <!-- ═══ OPORTUNIDADE (Valor + Produtos) ═══ -->
      <div
        v-if="!oculto('kanban_oportunidade')"
        class="border border-n-weak rounded-lg overflow-hidden"
      >
        <!-- Header accordion -->
        <button
          class="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
          @click="oportunidadeOpen = !oportunidadeOpen"
        >
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-circle-dollar-sign size-4" />
            {{ tCard('OPPORTUNITY') }}
            <span
              v-if="productsTotal > 0 || card.value"
              class="font-normal text-n-slate-9"
            >
              — {{ fmtMoney(productsTotal || card.value) }}
            </span>
          </span>
          <span
            class="size-3"
            :class="
              oportunidadeOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>

        <div
          v-show="oportunidadeOpen"
          class="p-3 flex flex-col gap-3 border-t border-n-weak"
        >
          <!-- Valor manual -->
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
            >
              {{ tCard('MANUAL_VALUE') }}
            </label>
            <div v-if="!editingValue" class="flex items-center gap-1">
              <span
                class="text-xs text-n-slate-12 cursor-pointer hover:text-n-blue-9"
                @click="startEditValue"
              >
                {{ card.value != null ? fmtMoney(card.value) : '—' }}
              </span>
              <button
                class="text-n-slate-7 hover:text-n-blue-9"
                :title="tCard('EDIT_VALUE')"
                @click="startEditValue"
              >
                <span class="i-lucide-pencil size-3" />
              </button>
            </div>
            <div v-else class="flex items-center gap-1">
              <input
                v-model="valueInput"
                type="number"
                step="0.01"
                class="!mb-0 flex-1 text-xs py-1"
                :placeholder="fmtMoney(0)"
                @keydown.enter="saveValue"
                @keydown.esc="editingValue = false"
              />
              <button
                class="text-green-600 px-1"
                :title="tCard('SAVE')"
                @click="saveValue"
              >
                <span class="i-lucide-check size-3" />
              </button>
              <button
                class="text-red-500 px-1"
                :title="tCard('CANCEL')"
                @click="editingValue = false"
              >
                <span class="i-lucide-x size-3" />
              </button>
            </div>
          </div>

          <!-- Produtos -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label
                class="text-[10px] font-bold uppercase tracking-wide text-n-slate-11"
              >
                {{ tCard('PRODUCTS') }}
              </label>
              <button
                class="text-[10px] text-n-blue-9 hover:underline"
                :disabled="saving"
                @click="openAddProduct"
              >
                + {{ tCard('ADD') }}
              </button>
            </div>

            <!-- Lista de produtos -->
            <div v-if="products.length" class="flex flex-col gap-1">
              <div
                v-for="item in products"
                :key="item.id"
                class="flex items-center justify-between text-xs border border-n-weak rounded-lg px-2 py-1.5"
              >
                <div class="flex flex-col min-w-0 flex-1">
                  <span class="text-n-slate-12 font-medium truncate">{{
                    item.productName
                  }}</span>
                  <span class="text-n-slate-9 text-[10px]">
                    {{ `${item.quantity}× ${fmtMoney(item.unitPrice)}` }}
                    <span v-if="item.discountPercentage">
                      {{
                        tCard('DISCOUNT_SUFFIX', {
                          percent: item.discountPercentage,
                        })
                      }}
                    </span>
                  </span>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span class="text-n-slate-12 font-semibold">{{
                    fmtMoney(productSubtotal(item))
                  }}</span>
                  <button
                    class="text-red-400 hover:text-red-600 text-[11px]"
                    :disabled="saving"
                    :title="tCard('REMOVE_PRODUCT')"
                    @click="removeProduct(item.id)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
              </div>

              <!-- Total dos produtos -->
              <div
                class="flex justify-between text-xs font-semibold pt-1 border-t border-n-weak"
              >
                <span class="text-n-slate-9">{{
                  tCard('PRODUCTS_TOTAL')
                }}</span>
                <span class="text-n-slate-12">{{
                  fmtMoney(productsTotal)
                }}</span>
              </div>
            </div>
            <p
              v-else-if="!loadingProducts"
              class="text-xs text-n-slate-9 italic"
            >
              {{ tCard('NO_PRODUCTS') }}
            </p>

            <!-- Formulário de adicionar produto -->
            <div
              v-if="showAddProduct"
              class="flex flex-col gap-2 p-2 border border-n-weak rounded-lg bg-n-surface-1"
            >
              <select
                v-model="pickProductId"
                class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-surface-1"
              >
                <option :value="null" disabled>
                  {{ tCard('SELECT_PRODUCT') }}
                </option>
                <option v-for="p in catalogProducts" :key="p.id" :value="p.id">
                  {{ p.name }} — {{ fmtMoney(p.unitPrice) }}
                </option>
              </select>
              <p
                v-if="!catalogProducts.length"
                class="text-[10px] text-n-slate-9 italic"
              >
                {{ tCard('EMPTY_CATALOG') }}
              </p>
              <div class="grid grid-cols-2 gap-1">
                <div class="flex flex-col gap-0.5">
                  <label class="text-[9px] text-n-slate-9 uppercase">{{
                    tCard('QTY')
                  }}</label>
                  <input
                    v-model="pickQty"
                    type="number"
                    min="1"
                    step="1"
                    class="!mb-0 text-xs py-1"
                  />
                </div>
                <div class="flex flex-col gap-0.5">
                  <label class="text-[9px] text-n-slate-9 uppercase">{{
                    tCard('DISCOUNT')
                  }}</label>
                  <input
                    v-model="pickDiscount"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="!mb-0 text-xs py-1"
                  />
                </div>
              </div>
              <div class="flex gap-1">
                <button
                  class="flex-1 py-1 text-xs rounded bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
                  :disabled="!pickProductId || saving"
                  @click="confirmAddProduct"
                >
                  {{ tCard('ADD') }}
                </button>
                <button
                  class="flex-1 py-1 text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
                  @click="showAddProduct = false"
                >
                  {{ tCard('CANCEL') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Indicador de salvando -->
      <p v-if="saving" class="text-[10px] text-n-blue-9 animate-pulse">
        {{ tCard('SAVING') }}
      </p>
    </div>
  </div>
</template>
